// FUNCTIONALITY-102/103/115C-R2 — Store V2 consent gate + Profile/About (legal SoT consumers).
import { useEffect, useRef, useState } from "react";
import type { InfoSection } from "./store-info-v2.js";
import { CONSENT_SHORT, CONSENT_VERSION, INFO_SECTIONS, LEGAL_SECTION_KINDS, STORE_INFO } from "./store-info-v2.js";
import { CONSENT_SHORT_EN, localizeInfoSection, localizeInfoSections } from "./store-info-i18n-v2.js";
import { ContactActionButton } from "./contact-actions-v2.js";
import {
  SellerContactsBlock,
  SellerPaymentBlock,
  ViewSellerDetailsButton,
} from "./seller-requisites-v2.js";
import type { StoreV2Lang } from "./store-v2-locale.js";
import { t } from "./store-v2-ui-copy.js";
import { applyStoreV2Theme, type StoreV2Theme } from "./theme-v2.js";
import { LegalDetailFooterNav } from "./LegalDetailFooterNav.js";
import { CustomerOrderDocumentsHomeV2 } from "./CustomerOrderDocumentsHomeV2.js";
import { TertiaryTextAction } from "./commerce-ui-v2.js";
import { LoadingBlock } from "./components.js";
import { CardBrandTrustBlock } from "./card-brand-trust-v2.js";
import { customerLegalRevisionLabel } from "./legal-customer-revision-v2.js";
import { LegalActionCard } from "./legal-action-card-v2.js";
import type { LegalHubPanel } from "./legal-document-registry-v2.js";
import { loadLegalSectionBody } from "./legal-body-loader-v2.js";
import { StoreV2GlobalClose } from "./components.js";
import { emitEvent } from "./events-v2.js";
import {
  archiveSavedAddress,
  readAddressBook,
  readCheckoutPrefill,
  readConsentAcceptedAt,
  setDefaultSavedAddress,
  telegramWebAppUser,
  upsertSavedAddress,
  writeCheckoutPrefill,
  type CustomerProfileV2,
  type CustomerSavedAddressV2,
} from "./customer-profile-v2.js";
import { AddressBookEditorSheetV2, type AddressBookEditorDraft } from "./AddressBookEditorSheetV2.js";
import { BuyerProfilesCabinetV2 } from "./BuyerProfilesCabinetV2.js";
import {
  deliveryMethodTitle,
  formatDeliveryDestinationLine,
} from "./delivery-profile-v2.js";

function InfoBlock({ section, open, lang = "ua" }: { section: InfoSection; open?: boolean; lang?: StoreV2Lang }) {
  const localized = localizeInfoSection(section, lang);
  const [lazyBody, setLazyBody] = useState<string[] | null>(null);
  useEffect(() => {
    const kind = localized.kind;
    if (kind !== "offer" && kind !== "privacy") return;
    let cancelled = false;
    void loadLegalSectionBody(kind, lang).then((lines) => {
      if (!cancelled && lines?.length) setLazyBody(lines);
    });
    return () => {
      cancelled = true;
    };
  }, [localized.kind, lang]);
  const isAbout = localized.kind === "about";
  const isOffer = localized.kind === "offer";
  const isPayment = localized.kind === "payment";
  const methods = localized.paymentMethods ?? [];
  const body = lazyBody ?? localized.body;

  return (
    <details
      className="sv2-info"
      open={open}
      data-legal-kind={localized.kind ?? "other"}
      id={localized.kind ? `sv2-legal-${localized.kind}` : undefined}
    >
      <summary className="sv2-info-summary">{localized.title}</summary>
      <div className="sv2-info-body">
        {body.map((p, i) =>
          p.split("\n").map((line, j) => (
            <p key={`${i}-${j}`}>{line}</p>
          )),
        )}

        {isAbout ? (
          <>
            <SellerContactsBlock lang={lang} testId="sv2-consent-contacts-once" />
            <SellerPaymentBlock lang={lang} testId="sv2-consent-fop-once" anchorId="sv2-seller-payment-block" />
          </>
        ) : null}

        {isOffer ? <ViewSellerDetailsButton lang={lang} /> : null}

        {isPayment && methods.length ? (
          <>
            <CardBrandTrustBlock lang={lang} testId="sv2-legal-payment-trust" />
            <p className="sv2-payment-methods-title">
              {lang === "en" ? "Payment methods" : "Способи оплати"}
            </p>
            <ul className="sv2-payment-methods-list">
              {methods.map((method, idx) => (
                <li key={method}>
                  {method}
                  {idx === 0 ? (
                    <SellerPaymentBlock
                      lang={lang}
                      testId="sv2-payment-fop-block"
                      showTitle={false}
                      compact
                    />
                  ) : null}
                </li>
              ))}
            </ul>
          </>
        ) : null}

        {localized.links?.length ? (
          <ul className="sv2-legal-links">
            {localized.links.map((l) => (
              <li key={l.href}>
                <ContactActionButton lang={lang} type="web" value={l.href} label={l.label} className="sv2-about-link" />
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </details>
  );
}

function LegalSections({
  firstOpen,
  lang = "ua",
  kinds,
}: {
  firstOpen?: boolean;
  lang?: StoreV2Lang;
  kinds?: Array<NonNullable<InfoSection["kind"]>>;
}) {
  const allowed = kinds ?? [...LEGAL_SECTION_KINDS];
  const sections = localizeInfoSections(INFO_SECTIONS, lang).filter((section) =>
    Boolean(section.kind && allowed.includes(section.kind)),
  );
  return (
    <>
      {sections.map((section, i) => (
        <InfoBlock key={`${section.kind ?? section.title}-${lang}`} section={section} open={Boolean(firstOpen && i === 0)} lang={lang} />
      ))}
    </>
  );
}

export interface ConsentGateProps {
  lang: StoreV2Lang;
  onLangChange: (lang: StoreV2Lang) => void;
  theme: StoreV2Theme;
  onThemeCycle: () => void;
  /** Accept legal package; marketingOptIn is independent optional permission. */
  onAccept: (marketingOptIn: boolean) => void;
  onDecline?: () => void;
  onOpenOffer?: () => void;
  onOpenPrivacy?: () => void;
  onDetails?: () => void;
  onOpenReviewTerms?: () => void;
  onBrowserCloseFallback?: () => void;
  busy?: boolean;
  error?: boolean;
  /** Reflect existing GRANTED without silent revoke (legal version change). */
  marketingAlreadyGranted?: boolean;
}

function useConsentTheme(theme: StoreV2Theme) {
  useEffect(() => {
    applyStoreV2Theme(theme);
  }, [theme]);
}

function ConsentLangThemeBar({
  lang,
  onLangChange,
  theme,
  onThemeCycle,
  onBrowserCloseFallback,
  routeClass = "consent",
}: {
  lang: StoreV2Lang;
  onLangChange: (lang: StoreV2Lang) => void;
  theme: StoreV2Theme;
  onThemeCycle: () => void;
  onBrowserCloseFallback?: () => void;
  routeClass?: string;
}) {
  const themeGlyph = theme === "dark" ? "☾" : theme === "light" ? "☀" : "◐";
  return (
    <div className="sv2-consent-toolbar" data-sv2-consent-toolbar="true">
      <StoreV2GlobalClose
        lang={lang}
        routeClass={routeClass}
        {...(onBrowserCloseFallback ? { onBrowserCloseFallback } : {})}
      />
      <LanguageSwitch lang={lang} onLangChange={onLangChange} />
      <button
        type="button"
        className="sv2-navbtn sv2-theme-toggle"
        data-sv2-theme-toggle="consent"
        onClick={onThemeCycle}
        aria-label={lang === "en" ? `Theme: ${theme}` : `Тема: ${theme}`}
        title={lang === "en" ? `Theme: ${theme}` : `Тема: ${theme}`}
      >
        {themeGlyph}
      </button>
    </div>
  );
}

/**
 * First-open consent overlay. Mandatory: the catalog is not rendered behind it,
 * so it cannot be skipped. Decline does not enter the commerce flow.
 */
export function ConsentGate({
  lang,
  onLangChange,
  theme,
  onThemeCycle,
  onAccept,
  onDecline,
  onOpenOffer,
  onOpenPrivacy,
  onDetails,
  onOpenReviewTerms,
  onBrowserCloseFallback,
  busy = false,
  error = false,
  marketingAlreadyGranted = false,
}: ConsentGateProps) {
  useConsentTheme(theme);
  const [declined, setDeclined] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(marketingAlreadyGranted);
  const consent = lang === "en" ? CONSENT_SHORT_EN : CONSENT_SHORT;
  const closeApp = () => {
    try {
      const tg = (window as unknown as { Telegram?: { WebApp?: { close?: () => void } } }).Telegram?.WebApp;
      tg?.close?.();
    } catch {
      /* ignore */
    }
    onDecline?.();
  };
  if (declined) {
    return (
      <div
        className="sv2-consent-overlay"
        data-sv2-consent="declined"
        role="dialog"
        aria-modal="true"
        aria-label={t(lang, "consent_decline_title")}
      >
        <div className="sv2-consent-card">
          <h2 className="sv2-consent-title">{STORE_INFO.name}</h2>
          <p className="sv2-consent-tag">{t(lang, "consent_decline_title")}</p>
          <div className="sv2-consent-body">
            <p>{t(lang, "consent_decline_body")}</p>
          </div>
          <button type="button" className="sv2-cta" onClick={closeApp} data-testid="sv2-consent-declined-close">
            {t(lang, "close")}
          </button>
        </div>
      </div>
    );
  }
  return (
    <div
      className="sv2-consent-overlay"
      data-sv2-consent="gate"
      data-consent-version={CONSENT_VERSION}
      role="dialog"
      aria-modal="true"
      aria-label={lang === "en" ? "User consent" : "Згода користувача"}
    >
      <div className="sv2-consent-card">
        <ConsentLangThemeBar
          lang={lang}
          onLangChange={onLangChange}
          theme={theme}
          onThemeCycle={onThemeCycle}
          {...(onBrowserCloseFallback ? { onBrowserCloseFallback } : {})}
          routeClass="consent_gate"
        />
        <div className="sv2-consent-emoji" aria-hidden="true" data-testid="sv2-consent-bowl">
          🍜
        </div>
        <h2 className="sv2-consent-title">{STORE_INFO.name}</h2>
        <p className="sv2-consent-tag">{t(lang, "consent_before_start")}</p>
        <div className="sv2-consent-body">
          {consent.body.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
        <div className="sv2-consent-legal-actions">
          <LegalActionCard
            lang={lang}
            icon="📄"
            title={lang === "en" ? "Public offer" : "Публічна оферта"}
            subtitle=""
            onClick={() => (onOpenOffer ?? onOpenReviewTerms)?.()}
            testId="sv2-consent-legal-offer"
          />
          <LegalActionCard
            lang={lang}
            icon="🔒"
            title={lang === "en" ? "Privacy policy" : "Політика конфіденційності"}
            subtitle=""
            onClick={() => (onOpenPrivacy ?? onOpenReviewTerms)?.()}
            testId="sv2-consent-legal-privacy"
          />
        </div>
        <label
          className="sv2-consent-marketing-optin"
          data-testid="sv2-consent-marketing-optin"
          data-default-off="true"
          data-checked={marketingOptIn ? "true" : "false"}
          aria-label={
            lang === "en"
              ? "Optional: receive Telegram new arrivals, personal offers, and unfinished-purchase reminders"
              : "За бажанням: отримувати в Telegram новинки, персональні пропозиції та нагадування про незавершені покупки"
          }
        >
          <input
            type="checkbox"
            checked={marketingOptIn}
            disabled={busy}
            onChange={(e) => setMarketingOptIn(e.target.checked)}
            data-testid="sv2-consent-marketing-checkbox"
            aria-checked={marketingOptIn}
          />
          <span className="sv2-consent-marketing-optin-copy">
            <span className="sv2-consent-marketing-optin-title">
              {lang === "en" ? "🎁 New arrivals & personal offers" : "🎁 Новинки та персональні пропозиції"}
            </span>
            {lang === "en"
              ? "Receive in Telegram new arrivals, personal offers, and reminders about unfinished purchases."
              : "Отримувати в Telegram новинки, персональні пропозиції та нагадування про незавершені покупки."}
            <span className="sv2-consent-marketing-optin-hint">
              {lang === "en" ? "Optional · turn off anytime." : "За бажанням · можна вимкнути будь-коли."}
            </span>
          </span>
        </label>
        {error ? (
          <p className="sv2-muted" style={{ fontSize: 13, margin: "0 0 8px" }} data-testid="sv2-consent-error">
            {lang === "en" ? "Could not save consent. Please retry." : "Не вдалося зберегти згоду. Повторіть спробу."}
          </p>
        ) : null}
        <button
          type="button"
          className="sv2-cta sv2-consent-accept-primary"
          onClick={() => onAccept(marketingOptIn)}
          disabled={busy}
          data-testid="sv2-consent-accept"
        >
          {busy
            ? lang === "en"
              ? "Saving…"
              : "Зберігаємо…"
            : error
              ? lang === "en"
                ? "Retry"
                : "Повторити"
              : t(lang, "consent_accept_continue")}
        </button>
        <button
          type="button"
          className="sv2-cta sv2-secondary"
          onClick={() => {
            setDeclined(true);
            if (onDetails && !onDecline) onDetails();
          }}
          disabled={busy}
          data-testid="sv2-consent-decline"
        >
          {t(lang, "consent_decline")}
        </button>
      </div>
    </div>
  );
}

export interface ConsentDetailsProps {
  lang: StoreV2Lang;
  onLangChange: (lang: StoreV2Lang) => void;
  theme: StoreV2Theme;
  onThemeCycle: () => void;
  onAccept: () => void;
  onBack: () => void;
  onBrowserCloseFallback?: () => void;
  busy?: boolean;
  error?: boolean;
}

/** Blocking legal details reached from the consent gate (before acceptance). */
export function ConsentDetails({
  lang,
  onLangChange,
  theme,
  onThemeCycle,
  onAccept,
  onBack,
  onBrowserCloseFallback,
  busy = false,
  error = false,
}: ConsentDetailsProps) {
  useConsentTheme(theme);
  return (
    <div
      className="sv2-consent-overlay"
      data-sv2-consent="details"
      role="dialog"
      aria-modal="true"
      aria-label={lang === "en" ? "User Agreement" : "Угода користувача"}
    >
      <div className="sv2-consent-card sv2-consent-details">
        <ConsentLangThemeBar
          lang={lang}
          onLangChange={onLangChange}
          theme={theme}
          onThemeCycle={onThemeCycle}
          {...(onBrowserCloseFallback ? { onBrowserCloseFallback } : {})}
          routeClass="consent_details"
        />
        <h2 className="sv2-consent-title">{STORE_INFO.name}</h2>
        <p className="sv2-consent-tag">{lang === "en" ? "Taste Asia!" : STORE_INFO.slogan}</p>
        <div className="sv2-consent-scroll">
          <LegalSections firstOpen lang={lang} kinds={[...LEGAL_SECTION_KINDS]} />
        </div>
        <button type="button" className="sv2-cta" onClick={onAccept} disabled={busy} data-testid="sv2-consent-details-accept">
          {busy
            ? lang === "en"
              ? "Saving…"
              : "Зберігаємо…"
            : error
              ? lang === "en"
                ? "Retry"
                : "Повторити"
              : t(lang, "agree")}
        </button>
        {error ? (
          <p className="sv2-muted" style={{ fontSize: 13 }} data-testid="sv2-consent-details-error">
            {lang === "en" ? "Could not save consent. Please retry." : "Не вдалося зберегти згоду. Повторіть спробу."}
          </p>
        ) : null}
        <button type="button" className="sv2-cta sv2-secondary" onClick={onBack} disabled={busy}>
          {t(lang, "back")}
        </button>
      </div>
    </div>
  );
}

export function LanguageSwitch({ lang, onLangChange }: { lang: StoreV2Lang; onLangChange: (lang: StoreV2Lang) => void }) {
  return (
    <div className="sv2-lang-switch sv2-consent-lang" role="group" aria-label={t(lang, "lang")}>
      <button type="button" className={`sv2-lang-btn${lang === "ua" ? " is-active" : ""}`} onClick={() => onLangChange("ua")}>UA</button>
      <button type="button" className={`sv2-lang-btn${lang === "en" ? " is-active" : ""}`} onClick={() => onLangChange("en")}>EN</button>
    </div>
  );
}

export interface ProfileV2Props {
  buildId: string;
  snapshotId?: string | null;
  generatedAt?: string | null;
  deleteRequested?: boolean;
  marketingSuppressed?: boolean;
  /** COMM-R1.2 — canonical marketing permission for Cabinet UX. */
  marketingPermissionState?: "UNKNOWN" | "GRANTED" | "WITHDRAWN" | "DENIED";
  lang?: "ua" | "en";
  cartCount?: number;
  hideTechnicalDiagnostics?: boolean;
  initialLegalPanel?: LegalHubPanel | null;
  /** Document-only surface (consent gate / checkout return) — Back skips legal hub. */
  standaloneDocument?: boolean;
  skipHubOnDocumentBack?: boolean;
  onLangChange?: (lang: StoreV2Lang) => void;
  theme?: StoreV2Theme;
  onThemeCycle?: () => void;
  onDeleteData?: () => void;
  onMarketingOptOut?: () => void;
  /** Explicit re-enable after WITHDRAWN — creates new GRANTED history event. */
  onMarketingOptIn?: () => void;
  onContactManager?: () => void;
  /** Leave profile route (stack back) — never used for document Back. */
  onLeaveProfile?: () => void;
  onOpenCart?: () => void;
  onOpenCatalog?: () => void;
  onCloseStore?: () => void;
  onBrowserCloseFallback?: () => void;
  // STAGING-LAUNCH-READINESS-002-R2 — customer cabinet entries reusing this hub.
  onOpenOrders?: () => void;
  /** COMMERCE-CORE-R1 — open order document bundle (order detail · Documents). */
  onOpenOrderDocuments?: (orderId: string) => void;
  /** Session for Cabinet order-documents home. */
  cabinetSessionKey?: string | null;
  onOpenMessages?: () => void;
  onOpenInvite?: () => void;
  onOpenEngagementHub?: () => void;
  /** COMMERCE-176C-R3 — cabinet root vs dedicated profile/delivery screens. */
  cabinetSurface?: "root" | "profile" | "delivery" | "buyer";
  onOpenProfileForm?: () => void;
  onOpenDeliveryAddresses?: () => void;
  /** BUYER-INFRA-R1 — commercial buyer identity (independent of delivery recipient). */
  onOpenBuyerProfiles?: () => void;
  /** CUSTOMER-R5 — initial Profile tab when opening Профіль. */
  initialProfileTab?: "personal" | "fop" | "company";
  consentAcceptedAt?: string | null;
  cabinetProfile?: {
    name: string;
    phone: string;
    city: string;
    warehouse: string;
    locale?: string | undefined;
  } | null;
}

function formatSnapshotDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

export function ProfileV2({
  buildId,
  snapshotId,
  generatedAt,
  deleteRequested,
  marketingSuppressed,
  marketingPermissionState = "UNKNOWN",
  lang = "ua",
  cartCount = 0,
  hideTechnicalDiagnostics = false,
  initialLegalPanel = null,
  standaloneDocument = false,
  skipHubOnDocumentBack = false,
  onLangChange,
  theme,
  onThemeCycle,
  onDeleteData,
  onMarketingOptOut,
  onMarketingOptIn,
  onLeaveProfile,
  onOpenCart,
  onOpenCatalog,
  onCloseStore,
  onBrowserCloseFallback,
  onOpenOrders,
  onOpenOrderDocuments,
  cabinetSessionKey = null,
  onOpenMessages,
  onOpenInvite,
  onOpenEngagementHub,
  cabinetSurface = "root",
  onOpenProfileForm,
  onOpenDeliveryAddresses,
  onOpenBuyerProfiles,
  initialProfileTab = "personal",
  consentAcceptedAt = null,
  cabinetProfile = null,
}: ProfileV2Props) {
  const social = STORE_INFO.social;
  const catalogDate = formatSnapshotDate(generatedAt);
  const [panel, setPanel] = useState<LegalHubPanel | null>(initialLegalPanel);
  const [profileTab, setProfileTab] = useState<"personal" | "fop" | "company">(initialProfileTab);
  useEffect(() => {
    setProfileTab(initialProfileTab);
  }, [initialProfileTab]);
  const tgUser = telegramWebAppUser();
  const [profileName, setProfileName] = useState(() => {
    const p = readCheckoutPrefill();
    return p?.name || tgUser.firstName || "";
  });
  const [profileSurname, setProfileSurname] = useState(() => {
    const p = readCheckoutPrefill();
    return p?.surname || tgUser.lastName || "";
  });
  const [profilePhone, setProfilePhone] = useState(() => readCheckoutPrefill()?.phone ?? "");
  const [profileEmail, setProfileEmail] = useState(() => readCheckoutPrefill()?.email ?? "");
  const [profileTelegram, setProfileTelegram] = useState(
    () => readCheckoutPrefill()?.telegram || tgUser.username || "",
  );
  const [profileViber, setProfileViber] = useState(() => readCheckoutPrefill()?.viber ?? "");
  const [preferredContact, setPreferredContact] = useState<"telegram" | "phone" | "email" | "viber">(
    () => readCheckoutPrefill()?.preferred_contact ?? "telegram",
  );
  const [birthday, setBirthday] = useState(() => {
    try {
      return readCheckoutPrefill()?.birthday ?? "";
    } catch {
      return "";
    }
  });
  const [addressesOpen, setAddressesOpen] = useState(false);
  const [docsHubOpen, setDocsHubOpen] = useState(false);
  const [orderDocsHomeOpen, setOrderDocsHomeOpen] = useState(false);
  const [addressBook, setAddressBook] = useState<CustomerSavedAddressV2[]>(() =>
    typeof window !== "undefined" ? readAddressBook() : [],
  );
  const [addressEditor, setAddressEditor] = useState<AddressBookEditorDraft | null>(null);
  const [archiveConfirmId, setArchiveConfirmId] = useState<string | null>(null);
  const [privacyMsg, setPrivacyMsg] = useState<string | null>(null);
  const [profileSaved, setProfileSaved] = useState(false);
  const [lazyDocBody, setLazyDocBody] = useState<string[] | null>(null);
  const scrollPositions = useRef<Partial<Record<LegalHubPanel, number>>>({});
  const returnToDocsHubRef = useRef(false);

  useEffect(() => {
    if (!panel) {
      setLazyDocBody(null);
      return;
    }
    const kind =
      panel === "about" || panel === "offer" || panel === "privacy" || panel === "payment" || panel === "returns"
        ? panel
        : null;
    if (kind !== "offer" && kind !== "privacy") {
      setLazyDocBody(null);
      return;
    }
    let cancelled = false;
    void loadLegalSectionBody(kind, lang).then((lines) => {
      if (!cancelled && lines?.length) setLazyDocBody(lines);
    });
    return () => {
      cancelled = true;
    };
  }, [panel, lang]);

  useEffect(() => {
    if (cabinetSurface !== "delivery") return;
    if (addressBook.length > 0 || addressEditor) return;
    setAddressEditor({
      id: `addr_${Date.now().toString(36)}`,
      label: lang === "en" ? "Branch" : "Відділення",
      name: profileName || "",
      surname: profileSurname || "",
      phone: profilePhone || "",
      city: "",
      warehouse: "",
      is_default: true,
      delivery_type: "NOVA_POSHTA_BRANCH",
    });
  }, [cabinetSurface, addressBook.length, addressEditor, lang, profileName, profilePhone, profileSurname]);

  useEffect(() => {
    if (initialLegalPanel) setPanel(initialLegalPanel);
  }, [initialLegalPanel]);

  useEffect(() => {
    if (!panel) return;
    const root = document.querySelector<HTMLElement>("[data-store-scroll-root='true']") ?? document.documentElement;
    const saved = scrollPositions.current[panel] ?? 0;
    requestAnimationFrame(() => root.scrollTo({ top: saved, behavior: "auto" }));
  }, [panel]);

  const docOrder: LegalHubPanel[] = [
    "about",
    "offer",
    "privacy",
    "payment",
    "returns",
    "requisites",
    "contacts",
    "data",
  ];

  useEffect(() => {
    const onPop = () => {
      setAddressesOpen(false);
      setDocsHubOpen(false);
      setPanel(null);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const openPanel = (next: LegalHubPanel) => {
    if (addressesOpen) setAddressesOpen(false);
    if (docsHubOpen) returnToDocsHubRef.current = true;
    setDocsHubOpen(false);
    if (panel) {
      try {
        const root = document.querySelector<HTMLElement>("[data-store-scroll-root='true']") ?? document.documentElement;
        scrollPositions.current[panel] = root.scrollTop;
      } catch {
        /* ignore */
      }
    }
    setPanel(next);
    try {
      window.history.pushState({ sv2LegalHub: next }, "");
    } catch {
      /* ignore */
    }
  };

  const openAddresses = () => {
    setAddressBook(readAddressBook());
    if (onOpenDeliveryAddresses) onOpenDeliveryAddresses();
    setAddressesOpen(false);
    setDocsHubOpen(false);
  };

  function persistProfilePatch(patch: Partial<CustomerProfileV2>) {
    const prev = readCheckoutPrefill();
    const payload: CustomerProfileV2 = {
      name: patch.name ?? profileName ?? prev?.name ?? "",
      surname: patch.surname ?? profileSurname ?? prev?.surname ?? "",
      phone: patch.phone ?? profilePhone ?? prev?.phone ?? "",
      city: prev?.city ?? cabinetProfile?.city ?? "",
      warehouse: prev?.warehouse ?? cabinetProfile?.warehouse ?? "",
    };
    const email = patch.email ?? profileEmail;
    const telegram = patch.telegram ?? profileTelegram;
    const viber = patch.viber ?? profileViber;
    const birthdayNext = patch.birthday ?? birthday;
    const preferred = patch.preferred_contact ?? preferredContact;
    if (email) payload.email = email;
    if (telegram) payload.telegram = telegram;
    if (viber) payload.viber = String(viber).slice(0, 32);
    if (birthdayNext) payload.birthday = birthdayNext;
    if (preferred) payload.preferred_contact = preferred;
    const locale = prev?.locale ?? cabinetProfile?.locale;
    if (locale) payload.locale = locale;
    if (prev?.np_selection) payload.np_selection = prev.np_selection;
    writeCheckoutPrefill(payload);
  }

  function saveProfileNow() {
    persistProfilePatch({
      name: profileName,
      surname: profileSurname,
      phone: profilePhone,
      email: profileEmail,
      telegram: profileTelegram,
      viber: profileViber,
      birthday,
      preferred_contact: preferredContact,
    });
    setProfileSaved(true);
  }

  const openDocsHub = () => {
    setDocsHubOpen(true);
    setOrderDocsHomeOpen(false);
    setAddressesOpen(false);
    setPanel(null);
    try {
      window.history.pushState({ sv2CabinetPanel: "docs" }, "");
    } catch {
      /* ignore */
    }
  };

  const openOrderDocsHome = () => {
    setOrderDocsHomeOpen(true);
    setDocsHubOpen(false);
    setAddressesOpen(false);
    setPanel(null);
    try {
      window.history.pushState({ sv2CabinetPanel: "order-docs" }, "");
    } catch {
      /* ignore */
    }
  };

  /** One logical step: document → hub; hub → leave profile. Never catalog via Back. */
  const legalBack = () => {
    if (addressesOpen) {
      try {
        if (window.history.state?.sv2CabinetPanel === "addresses") {
          window.history.back();
          return;
        }
      } catch {
        /* ignore */
      }
      setAddressesOpen(false);
      return;
    }
    if (docsHubOpen) {
      try {
        if (window.history.state?.sv2CabinetPanel === "docs") {
          window.history.back();
          return;
        }
      } catch {
        /* ignore */
      }
      setDocsHubOpen(false);
      return;
    }
    if (orderDocsHomeOpen) {
      try {
        if (window.history.state?.sv2CabinetPanel === "order-docs") {
          window.history.back();
          return;
        }
      } catch {
        /* ignore */
      }
      setOrderDocsHomeOpen(false);
      return;
    }
    if (panel) {
      if (standaloneDocument || skipHubOnDocumentBack) {
        onLeaveProfile?.();
        return;
      }
      setPanel(null);
      if (returnToDocsHubRef.current) {
        returnToDocsHubRef.current = false;
        setDocsHubOpen(true);
        return;
      }
      try {
        if (window.history.state?.sv2LegalHub) window.history.back();
      } catch {
        /* ignore */
      }
      return;
    }
    onLeaveProfile?.();
  };

  const docIdx = panel ? docOrder.indexOf(panel) : -1;
  const showAllDocuments = !standaloneDocument && !skipHubOnDocumentBack;
  const docNav =
    panel && docIdx >= 0
      ? {
          ...(docIdx > 0
            ? {
                onPrev: () => {
                  const prev = docOrder[docIdx - 1];
                  if (prev) openPanel(prev);
                },
              }
            : {}),
          ...(docIdx < docOrder.length - 1
            ? {
                onNext: () => {
                  const next = docOrder[docIdx + 1];
                  if (next) openPanel(next);
                },
              }
            : {}),
        }
      : undefined;

  const navTiles: Array<[LegalHubPanel, string, string, string]> =
    lang === "en"
      ? [
          ["about", "🏪", "About the store", "Who we are and hours"],
          ["offer", "📄", "Terms of use", "Public offer"],
          ["privacy", "🔒", "Privacy policy", "How we handle data"],
          ["payment", "💳", "Delivery & payment", "IBAN, Nova Poshta"],
          ["returns", "↩️", "Returns & exchange", "Food return rules"],
          ["requisites", "🏦", "Seller details", "FOP / bank requisites"],
          ["contacts", "📞", "Contacts", "Phone, email, address"],
        ]
      : [
          ["about", "🏪", "Про магазин", "Хто ми і графік роботи"],
          ["offer", "📄", "Умови користування", "Договір оферти"],
          ["privacy", "🔒", "Політика конфіденційності", "Як обробляємо дані"],
          ["payment", "💳", "Доставка й оплата", "IBAN, Нова Пошта"],
          ["returns", "↩️", "Повернення та обмін", "Правила для харчових товарів"],
          ["requisites", "🏦", "Реквізити продавця", "ФОП / банківські реквізити"],
          ["contacts", "📞", "Контакти", "Телефон, email, адреса"],
        ];

  // COMMERCE-176D-R1 — address book lives inline in Delivery tab (no intermediate page).
  if (false && addressesOpen) {
    const emptyDraft = (): AddressBookEditorDraft => ({
      id: `addr_${Date.now().toString(36)}`,
      label: lang === "en" ? "Branch" : "Відділення",
      name: "",
      surname: "",
      phone: "",
      city: "",
      warehouse: "",
      is_default: addressBook.length === 0,
      delivery_type: "NOVA_POSHTA_BRANCH",
    });
    return (
      <div className="sv2-body" data-testid="sv2-my-addresses" data-sv2-about-surface="true">
        <div className="sv2-section-title">{lang === "en" ? "My addresses" : "Мої адреси"}</div>
        <p className="sv2-muted" style={{ marginBottom: 12 }}>
          {lang === "en"
            ? "Saved Nova Poshta delivery profiles for this Telegram account. Editing here never changes historic orders."
            : "Збережені профілі доставки Новою поштою для цього Telegram-акаунта. Зміни тут не змінюють історичні замовлення."}
        </p>
        {addressBook.length === 0 ? (
          <div className="sv2-info-card sv2-cabinet-card">
            {lang === "en" ? "No saved delivery profiles yet." : "Збережених профілів доставки ще немає."}
          </div>
        ) : (
          addressBook.map((addr) => (
            <div
              key={addr.id}
              className="sv2-info-card sv2-cabinet-card sv2-delivery-profile-card"
              data-testid="sv2-address-card"
              data-delivery-type={addr.delivery_type ?? "NOVA_POSHTA_BRANCH"}
            >
              <div className="sv2-order-list-row">
                <strong data-testid="sv2-address-card-title">
                  {deliveryMethodTitle(lang, addr.delivery_type ?? "NOVA_POSHTA_BRANCH")}
                  {addr.is_default ? (lang === "en" ? " · primary" : " · основна") : ""}
                </strong>
              </div>
              <div className="sv2-muted" style={{ fontSize: 13 }}>
                {[addr.name, addr.surname].filter(Boolean).join(" ")}
              </div>
              {addr.phone ? <div style={{ fontSize: 13 }}>{addr.phone}</div> : null}
              <div style={{ marginTop: 4, fontSize: 13 }}>
                {addr.city ? (addr.city.startsWith("м.") || addr.city.startsWith("м ") ? addr.city : `м. ${addr.city}`) : ""}
              </div>
              <div style={{ marginTop: 2, fontSize: 13 }} data-testid="sv2-address-card-destination">
                {formatDeliveryDestinationLine(addr)}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
                {!addr.is_default ? (
                  <button
                    type="button"
                    className="sv2-cta sv2-secondary sv2-cta-compact"
                    data-testid="sv2-address-set-default"
                    onClick={() => setAddressBook(setDefaultSavedAddress(addr.id))}
                  >
                    {lang === "en" ? "Make primary" : "Зробити основною"}
                  </button>
                ) : (
                  <span className="sv2-pill" data-testid="sv2-address-primary-badge">
                    {lang === "en" ? "Primary" : "Основна"}
                  </span>
                )}
                <button
                  type="button"
                  className="sv2-cta sv2-secondary sv2-cta-compact"
                  data-testid="sv2-address-edit"
                  onClick={() => {
                    const draft: AddressBookEditorDraft = {
                      id: addr.id,
                      label: addr.label,
                      name: addr.name,
                      surname: addr.surname,
                      phone: addr.phone,
                      city: addr.city,
                      warehouse: addr.warehouse,
                      is_default: addr.is_default,
                      delivery_type: addr.delivery_type ?? "NOVA_POSHTA_BRANCH",
                    };
                    if (addr.street) draft.street = addr.street;
                    if (addr.building) draft.building = addr.building;
                    if (addr.apartment) draft.apartment = addr.apartment;
                    if (addr.entrance) draft.entrance = addr.entrance;
                    if (addr.floor) draft.floor = addr.floor;
                    if (addr.intercom) draft.intercom = addr.intercom;
                    if (addr.comment) draft.comment = addr.comment;
                    if (addr.display_address) draft.display_address = addr.display_address;
                    if (addr.address_source) draft.address_source = addr.address_source;
                    if (addr.np_selection) draft.np_selection = addr.np_selection;
                    setAddressEditor(draft);
                  }}
                >
                  {lang === "en" ? "Edit" : "Редагувати"}
                </button>
                <button
                  type="button"
                  className="sv2-cta sv2-destructive sv2-cta-compact"
                  data-testid="sv2-address-delete"
                  onClick={() => setArchiveConfirmId(addr.id)}
                >
                  {lang === "en" ? "Delete" : "Видалити"}
                </button>
              </div>
            </div>
          ))
        )}
        <button
          type="button"
          className="sv2-cta sv2-cta-compact"
          style={{ marginTop: 12 }}
          data-testid="sv2-address-add"
          onClick={() => setAddressEditor(emptyDraft())}
          disabled={Boolean(addressEditor)}
        >
          {lang === "en" ? "+ Add address" : "+ Додати адресу"}
        </button>
        {addressEditor ? (
          <div style={{ marginTop: 10 }} data-testid="sv2-address-editor-host">
            <AddressBookEditorSheetV2
              lang={lang}
              initial={addressEditor as AddressBookEditorDraft}
              onCancel={() => setAddressEditor(null)}
              onSave={(next) => {
                setAddressBook(upsertSavedAddress(next));
                setAddressEditor(null);
              }}
            />
          </div>
        ) : null}
        {archiveConfirmId ? (
          <div
            className="sv2-modal-overlay"
            role="dialog"
            aria-modal="true"
            data-testid="sv2-address-archive-confirm"
          >
            <div className="sv2-modal-card">
              <p style={{ marginTop: 0 }}>
                {lang === "en" ? "Archive this address?" : "Архівувати цю адресу?"}
              </p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="sv2-cta sv2-destructive"
                  data-testid="sv2-address-archive-yes"
                  onClick={() => {
                    const id = archiveConfirmId;
                    if (id) setAddressBook(archiveSavedAddress(id));
                    setArchiveConfirmId(null);
                  }}
                >
                  {lang === "en" ? "Archive" : "Архівувати"}
                </button>
                <button
                  type="button"
                  className="sv2-cta sv2-secondary"
                  data-testid="sv2-address-archive-no"
                  onClick={() => setArchiveConfirmId(null)}
                >
                  {lang === "en" ? "Cancel" : "Скасувати"}
                </button>
              </div>
            </div>
          </div>
        ) : null}
        <LegalDetailFooterNav
          lang={lang}
          cartCount={cartCount}
          onBack={legalBack}
          {...(onOpenCart ? { onCart: onOpenCart } : {})}
          {...(onOpenCatalog ? { onCatalog: onOpenCatalog } : {})}
          testId="sv2-addresses-footer-nav"
        />
      </div>
    );
  }

  if (orderDocsHomeOpen && cabinetSessionKey) {
    return (
      <CustomerOrderDocumentsHomeV2
        lang={lang}
        sessionKey={cabinetSessionKey}
        onOpenOrderDocuments={(orderId) => {
          setOrderDocsHomeOpen(false);
          onOpenOrderDocuments?.(orderId);
        }}
        onOpenLegalHub={openDocsHub}
        onBack={() => {
          setOrderDocsHomeOpen(false);
        }}
      />
    );
  }

  if (docsHubOpen) {
    return (
      <div className="sv2-body" data-testid="sv2-docs-hub" data-sv2-about-surface="true">
        <div className="sv2-section-title">
          {lang === "en" ? "About the store & documents" : "Про магазин і документи"}
        </div>
        <p className="sv2-muted" style={{ marginBottom: 12 }}>
          {lang === "en"
            ? "Legal documents, seller details, and store information."
            : "Юридичні документи, реквізити продавця та інформація про магазин."}
        </p>
        {(() => {
          const acceptedAt = consentAcceptedAt ?? readConsentAcceptedAt(CONSENT_VERSION);
          if (!acceptedAt) return null;
          const d = new Date(acceptedAt);
          const label = Number.isNaN(d.getTime())
            ? acceptedAt.slice(0, 10)
            : d.toLocaleDateString(lang === "en" ? "en-GB" : "uk-UA");
          return (
            <p className="sv2-muted" style={{ marginBottom: 12, fontSize: 13 }} data-testid="sv2-legal-consent-status">
              {lang === "en" ? `Terms accepted · ${label}` : `Умови прийнято · ${label}`}
            </p>
          );
        })()}
        <nav className="sv2-about-bento" data-testid="sv2-docs-hub-bento" aria-label={lang === "en" ? "Documents" : "Документи"}>
          {navTiles.map(([id, icon, title, hint]) => (
            <button
              key={id}
              type="button"
              className="sv2-about-bento-tile"
              data-testid={`sv2-legal-hub-${id}`}
              onClick={() => openPanel(id)}
            >
              <span className="sv2-about-bento-icon" aria-hidden="true">
                {icon}
              </span>
              <strong>{title}</strong>
              <span>{hint}</span>
            </button>
          ))}
          <button
            type="button"
            className="sv2-about-bento-tile"
            data-testid="sv2-legal-hub-data"
            onClick={() => openPanel("data")}
          >
            <span className="sv2-about-bento-icon" aria-hidden="true">
              🗂️
            </span>
            <strong>{lang === "en" ? "Notifications & privacy" : "Сповіщення та приватність"}</strong>
            <span>{lang === "en" ? "Orders, marketing, data rights" : "Замовлення, маркетинг, права на дані"}</span>
          </button>
        </nav>
        <LegalDetailFooterNav
          lang={lang}
          cartCount={cartCount}
          onBack={legalBack}
          {...(onOpenCart ? { onCart: onOpenCart } : {})}
          {...(onOpenCatalog ? { onCatalog: onOpenCatalog } : {})}
          testId="sv2-docs-hub-footer-nav"
        />
      </div>
    );
  }

  if (panel) {
    const sectionKind =
      panel === "about" || panel === "offer" || panel === "privacy" || panel === "payment" || panel === "returns"
        ? panel
        : null;
    const section = sectionKind
      ? localizeInfoSections(INFO_SECTIONS, lang).find((s) => s.kind === sectionKind)
      : null;
    const title =
      panel === "data"
        ? lang === "en"
          ? "Notifications & privacy"
          : "Сповіщення та приватність"
        : (navTiles.find((t) => t[0] === panel)?.[2] ??
          (lang === "en" ? "Document" : "Документ"));

    return (
      <div
        className="sv2-body sv2-legal-hub-panel sv2-legal-document-shell"
        data-sv2-about-surface="true"
        data-testid="sv2-legal-hub-panel"
        data-panel={panel}
      >
        {onLangChange && theme && onThemeCycle ? (
          <div className="sv2-legal-doc-chrome" data-testid="sv2-legal-doc-chrome">
            <LanguageSwitch lang={lang} onLangChange={onLangChange} />
            <button
              type="button"
              className="sv2-navbtn sv2-theme-toggle"
              onClick={onThemeCycle}
              aria-label={lang === "en" ? "Theme" : "Тема"}
            >
              {theme === "dark" ? "☾" : theme === "light" ? "☀" : "◐"}
            </button>
          </div>
        ) : null}
        <button type="button" className="sv2-cta sv2-secondary sv2-legal-doc-back-compact" data-testid="sv2-legal-hub-back" onClick={legalBack}>
          {lang === "en" ? "← Back" : "← Назад"}
        </button>
        <div className="sv2-section-title" style={{ marginTop: 12 }}>
          {title}
        </div>
        <p className="sv2-muted" style={{ fontSize: 12 }}>
          {lang === "en" ? "Store information" : "Інформація магазину"} · {STORE_INFO.name}
        </p>
        {panel === "offer" || panel === "privacy" ? (
          <p className="sv2-muted sv2-legal-customer-revision" style={{ fontSize: 12 }} data-testid="sv2-legal-customer-revision">
            {customerLegalRevisionLabel(lang)}
          </p>
        ) : null}

        {panel === "contacts" ? <SellerContactsBlock lang={lang} /> : null}
        {panel === "requisites" ? (
          <SellerPaymentBlock lang={lang} anchorId="sv2-seller-payment-block" />
        ) : null}
        {panel === "about" ? (
          <div className="sv2-profile-head" style={{ marginTop: 12 }}>
            <div className="sv2-profile-name">{STORE_INFO.name}</div>
            <div className="sv2-muted">{lang === "en" ? "Taste Asia!" : STORE_INFO.slogan}</div>
            <div className="sv2-muted" style={{ marginTop: 6, whiteSpace: "pre-line" }}>
              {lang === "en"
                ? "Business hours: Tue–Sun, 09:00–18:00. Monday — closed."
                : STORE_INFO.scheduleLine}
            </div>
          </div>
        ) : null}
        {panel === "data" ? (
          <div style={{ marginTop: 12 }} data-testid="sv2-privacy-data-panel">
            <section className="sv2-privacy-block" data-testid="sv2-privacy-orders-block">
              <h3 className="sv2-privacy-block-title">
                {lang === "en" ? "Order notifications" : "Сповіщення про замовлення"}
              </h3>
              <p className="sv2-privacy-block-status" data-testid="sv2-order-notify-status">
                {lang === "en" ? "Status: " : "Статус: "}
                <strong>{lang === "en" ? "Active" : "Активні"}</strong>
                <span className="sv2-muted" style={{ display: "block", marginTop: 4, fontSize: 12 }}>
                  {lang === "en"
                    ? "Service messages about payment and delivery stay on."
                    : "Сервісні повідомлення про оплату та доставку залишаються увімкненими."}
                </span>
              </p>
            </section>

            <section className="sv2-privacy-block" data-testid="sv2-privacy-marketing-block">
              <h3 className="sv2-privacy-block-title">
                {lang === "en" ? "Marketing messages" : "Маркетингові повідомлення"}
              </h3>
              <p className="sv2-privacy-block-status" data-testid="sv2-marketing-status">
                {lang === "en" ? "Status: " : "Статус: "}
                <strong>
                  {marketingPermissionState === "GRANTED"
                    ? lang === "en"
                      ? "On"
                      : "Увімкнено"
                    : marketingPermissionState === "WITHDRAWN" ||
                        marketingPermissionState === "DENIED" ||
                        marketingSuppressed
                      ? lang === "en"
                        ? "Off"
                        : "Вимкнено"
                      : lang === "en"
                        ? "Not set"
                        : "Не налаштовано"}
                </strong>
              </p>
              {marketingPermissionState === "GRANTED" ? (
                <button
                  type="button"
                  className="sv2-cta"
                  data-testid="sv2-marketing-opt-out"
                  onClick={() => {
                    onMarketingOptOut?.();
                    setPrivacyMsg(
                      lang === "en"
                        ? "Marketing messages disabled. Service messages about orders will remain."
                        : "Маркетингові повідомлення вимкнено. Сервісні повідомлення по замовленнях залишаться.",
                    );
                  }}
                >
                  {lang === "en" ? "Do not send me marketing" : "Не надсилати мені маркетинг"}
                </button>
              ) : (
                <button
                  type="button"
                  className="sv2-cta"
                  data-testid="sv2-marketing-opt-in"
                  onClick={() => {
                    onMarketingOptIn?.();
                    setPrivacyMsg(
                      lang === "en"
                        ? "Marketing messages enabled. You can turn them off anytime."
                        : "Маркетингові повідомлення увімкнено. Вимкнути можна будь-коли.",
                    );
                  }}
                >
                  {lang === "en" ? "Receive marketing" : "Отримувати маркетинг"}
                </button>
              )}
              {marketingPermissionState === "UNKNOWN" && !marketingSuppressed ? (
                <button
                  type="button"
                  className="sv2-cta sv2-secondary"
                  data-testid="sv2-marketing-opt-out-unknown"
                  style={{ marginTop: 8 }}
                  onClick={() => {
                    onMarketingOptOut?.();
                    setPrivacyMsg(
                      lang === "en"
                        ? "Marketing messages disabled. Service messages about orders will remain."
                        : "Маркетингові повідомлення вимкнено. Сервісні повідомлення по замовленнях залишаться.",
                    );
                  }}
                >
                  {lang === "en" ? "Do not send me marketing" : "Не надсилати мені маркетинг"}
                </button>
              ) : null}
            </section>

            <section className="sv2-privacy-block sv2-privacy-block-data" data-testid="sv2-privacy-data-rights-block">
              <h3 className="sv2-privacy-block-title">
                {lang === "en" ? "Personal data" : "Персональні дані"}
              </h3>
              <p className="sv2-muted" style={{ fontSize: 12, margin: "0 0 8px", lineHeight: 1.4 }}>
                {lang === "en"
                  ? "We will process your request considering accounting and order-support requirements. Transaction records needed by law are not erased instantly."
                  : "Запит буде опрацьовано з урахуванням вимог обліку та підтримки замовлень. Документи, потрібні за законом, не зникають миттєво."}
              </p>
              <button
                type="button"
                className="sv2-cta sv2-secondary"
                disabled={deleteRequested}
                data-testid="sv2-data-delete-request"
                onClick={() => {
                  onDeleteData?.();
                  setPrivacyMsg(
                    lang === "en"
                      ? "Request accepted. We will process it considering accounting and order-support requirements."
                      : "Запит прийнято. Ми обробимо його з урахуванням чинних вимог до обліку та підтримки замовлень.",
                  );
                }}
              >
                {lang === "en"
                  ? "Request deletion / anonymization of personal data"
                  : "Запит на видалення / знеособлення персональних даних"}
              </button>
            </section>
            {privacyMsg ? <div className="sv2-alert sv2-alert-ok">{privacyMsg}</div> : null}
          </div>
        ) : null}

        {section ? (
          <div className="sv2-legal-doc-body sv2-legal-doc-scroll" data-testid="sv2-legal-doc-body">
            {section.kind === "payment" ? <CardBrandTrustBlock lang={lang} testId="sv2-legal-hub-payment-trust" /> : null}
            {(lazyDocBody ?? section.body).length === 0 &&
            (section.kind === "offer" || section.kind === "privacy") ? (
              <LoadingBlock label={lang === "en" ? "Loading document…" : "Завантаження документа…"} />
            ) : null}
            {(lazyDocBody ?? section.body).map((p, i) =>
              p.split("\n").map((line, j) => <p key={`${i}-${j}`}>{line}</p>),
            )}
            {section.kind === "payment" && section.paymentMethods?.length ? (
              <ul className="sv2-payment-methods-list">
                {section.paymentMethods.map((method) => (
                  <li key={method}>{method}</li>
                ))}
              </ul>
            ) : null}
            {section.links?.length ? (
              <ul className="sv2-legal-links">
                {section.links.map((l) => (
                  <li key={l.href}>
                    <ContactActionButton lang={lang} type="web" value={l.href} label={l.label} className="sv2-about-link" />
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        <LegalDetailFooterNav
          lang={lang}
          cartCount={standaloneDocument || skipHubOnDocumentBack ? 0 : cartCount}
          onBack={legalBack}
          {...(showAllDocuments ? { onAllDocuments: () => setPanel(null) } : {})}
          {...(onOpenCart ? { onCart: onOpenCart } : {})}
          {...(onOpenCatalog ? { onCatalog: onOpenCatalog } : {})}
          {...(docNav ? { docNav } : {})}
          testId="sv2-legal-footer-nav"
        />
      </div>
    );
  }

  return (
    <div className="sv2-body" data-sv2-about-surface="true" data-testid="sv2-legal-hub">
      <div className="sv2-section-title">
        {cabinetSurface === "profile" || cabinetSurface === "buyer"
          ? lang === "en"
            ? "Profile"
            : "Профіль"
          : cabinetSurface === "delivery"
            ? lang === "en"
              ? "My delivery addresses"
              : "Мої адреси доставки"
              : lang === "en"
                ? "My cabinet"
                : "Мій кабінет"}
      </div>

      <>
          {cabinetSurface === "profile" || cabinetSurface === "buyer" ? (
            <div data-testid="sv2-cabinet-profile-panel">
              <div className="sv2-profile-tabs" role="tablist" data-testid="sv2-profile-tabs">
                <button
                  type="button"
                  role="tab"
                  className="sv2-cta sv2-secondary sv2-profile-tab"
                  aria-selected={profileTab === "personal"}
                  data-testid="sv2-profile-tab-personal"
                  onClick={() => setProfileTab("personal")}
                >
                  {lang === "en" ? "Personal" : "Особисті дані"}
                </button>
                <button
                  type="button"
                  role="tab"
                  className="sv2-cta sv2-secondary sv2-profile-tab"
                  aria-selected={profileTab === "fop"}
                  data-testid="sv2-profile-tab-fop"
                  onClick={() => setProfileTab("fop")}
                >
                  {lang === "en" ? "FOP" : "ФОП"}
                </button>
                <button
                  type="button"
                  role="tab"
                  className="sv2-cta sv2-secondary sv2-profile-tab"
                  aria-selected={profileTab === "company"}
                  data-testid="sv2-profile-tab-company"
                  onClick={() => setProfileTab("company")}
                >
                  {lang === "en" ? "Company" : "Компанія"}
                </button>
              </div>
              {profileTab === "fop" ? (
                <BuyerProfilesCabinetV2 lang={lang === "en" ? "en" : "ua"} focusType="FOP" />
              ) : profileTab === "company" ? (
                <BuyerProfilesCabinetV2 lang={lang === "en" ? "en" : "ua"} focusType="LEGAL_ENTITY" />
              ) : (
              <>
              <label className="sv2-address-field" style={{ display: "block", marginBottom: 10 }}>
                <span className="sv2-address-label">{lang === "en" ? "Name" : "Імʼя"}</span>
                <input
                  className="sv2-input sv2-input-visible"
                  value={profileName}
                  data-testid="sv2-cabinet-name"
                  onChange={(e) => {
                    setProfileName(e.target.value);
                    setProfileSaved(false);
                  }}
                />
              </label>
              <label className="sv2-address-field" style={{ display: "block", marginBottom: 10 }}>
                <span className="sv2-address-label">{lang === "en" ? "Surname" : "Прізвище"}</span>
                <input
                  className="sv2-input sv2-input-visible"
                  value={profileSurname}
                  data-testid="sv2-cabinet-surname"
                  onChange={(e) => {
                    setProfileSurname(e.target.value);
                    setProfileSaved(false);
                  }}
                />
              </label>
              <label className="sv2-address-field" style={{ display: "block", marginBottom: 10 }}>
                <span className="sv2-address-label">{lang === "en" ? "Phone" : "Телефон"}</span>
                <input
                  className="sv2-input sv2-input-visible"
                  value={profilePhone}
                  data-testid="sv2-cabinet-phone"
                  onChange={(e) => {
                    setProfilePhone(e.target.value);
                    setProfileSaved(false);
                  }}
                />
              </label>
              <label className="sv2-address-field" style={{ display: "block", marginBottom: 10 }}>
                <span className="sv2-address-label">Telegram</span>
                <input
                  className="sv2-input sv2-input-visible"
                  value={profileTelegram}
                  data-testid="sv2-cabinet-telegram"
                  placeholder={tgUser.username ? `@${tgUser.username}` : "@username"}
                  onChange={(e) => {
                    setProfileTelegram(e.target.value);
                    setProfileSaved(false);
                  }}
                />
                {tgUser.username ? (
                  <span className="sv2-muted" style={{ display: "block", marginTop: 4, fontSize: 12 }}>
                    {lang === "en"
                      ? "Prefill from Telegram WebApp when available."
                      : "Підставлено з Telegram WebApp, якщо доступно."}
                  </span>
                ) : null}
              </label>
              <label className="sv2-address-field" style={{ display: "block", marginBottom: 10 }}>
                <span className="sv2-address-label">Email</span>
                <input
                  type="email"
                  className="sv2-input sv2-input-visible"
                  value={profileEmail}
                  data-testid="sv2-cabinet-email"
                  onChange={(e) => {
                    setProfileEmail(e.target.value);
                    setProfileSaved(false);
                  }}
                />
              </label>
              <label className="sv2-address-field" style={{ display: "block", marginBottom: 10 }}>
                <span className="sv2-address-label">Viber</span>
                <input
                  type="tel"
                  className="sv2-input sv2-input-visible"
                  value={profileViber}
                  data-testid="sv2-cabinet-viber"
                  placeholder={lang === "en" ? "Phone for Viber" : "Телефон для Viber"}
                  maxLength={32}
                  onChange={(e) => {
                    setProfileViber(e.target.value.slice(0, 32));
                    setProfileSaved(false);
                  }}
                />
                <span className="sv2-muted" style={{ display: "block", marginTop: 4, fontSize: 12 }}>
                  {lang === "en"
                    ? "Optional. Saved as a contact preference only."
                    : "Необовʼязково. Зберігається лише як зручний спосіб звʼязку."}
                </span>
              </label>
              <label className="sv2-address-field" style={{ display: "block", marginBottom: 12 }}>
                <span className="sv2-address-label">
                  {lang === "en" ? "Birthday (optional)" : "День народження (необовʼязково)"}
                </span>
                <input
                  type="date"
                  className="sv2-input sv2-input-visible"
                  value={birthday}
                  data-testid="sv2-cabinet-birthday"
                  onChange={(e) => {
                    setBirthday(e.target.value);
                    setProfileSaved(false);
                  }}
                />
                <span className="sv2-muted" style={{ display: "block", marginTop: 6, fontSize: 12 }}>
                  {lang === "en"
                    ? "🎁 A little gift may wait for you on your birthday."
                    : "🎁 На день народження на вас може чекати невеликий подарунок."}
                </span>
              </label>
              <div style={{ marginBottom: 12 }} data-testid="sv2-preferred-contact">
                <div className="sv2-address-label" style={{ marginBottom: 6 }}>
                  {lang === "en" ? "Preferred contact" : "Зручний спосіб звʼязку"}
                </div>
                <div className="sv2-contact-chips" style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {(
                    [
                      ["telegram", "Telegram"],
                      ["phone", lang === "en" ? "Phone" : "Телефон"],
                      ["email", "Email"],
                      ["viber", "Viber"],
                    ] as const
                  ).map(([id, label]) => (
                    <button
                      key={id}
                      type="button"
                      className={`sv2-contact-chip ${preferredContact === id ? "is-active" : ""}`}
                      data-testid={`sv2-preferred-contact-${id}`}
                      onClick={() => {
                        setPreferredContact(id);
                        setProfileSaved(false);
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <button
                type="button"
                className="sv2-cta"
                style={{ width: "100%", marginTop: 8 }}
                data-testid="sv2-cabinet-profile-save"
                onClick={saveProfileNow}
              >
                {lang === "en" ? "Save" : "Зберегти"}
              </button>
              {profileSaved ? (
                <p className="sv2-muted" style={{ fontSize: 13, marginTop: 8 }} data-testid="sv2-cabinet-profile-saved">
                  {lang === "en" ? "Profile saved." : "Профіль збережено."}
                </p>
              ) : null}
              </>
              )}
            </div>
          ) : cabinetSurface === "delivery" ? (
            <div data-testid="sv2-cabinet-delivery-panel">
              <div data-testid="sv2-my-addresses">
              <p className="sv2-muted" style={{ marginBottom: 12, fontSize: 13 }}>
                {lang === "en"
                  ? "Saved Nova Poshta delivery profiles. Recipient is independent of buyer identity. Editing here never changes historic orders."
                  : "Збережені профілі доставки Новою поштою. Одержувач незалежний від покупця. Зміни тут не змінюють історичні замовлення."}
              </p>
              {addressBook.length === 0 ? (
                <div className="sv2-info-card sv2-cabinet-card" data-testid="sv2-delivery-empty">
                  {lang === "en" ? "No saved delivery addresses yet." : "Збережених адрес доставки ще немає."}
                </div>
              ) : (
                addressBook.map((addr) => (
                  <div
                    key={addr.id}
                    className="sv2-info-card sv2-cabinet-card sv2-delivery-profile-card"
                    data-testid="sv2-address-card"
                    data-delivery-type={addr.delivery_type ?? "NOVA_POSHTA_BRANCH"}
                  >
                    <div className="sv2-order-list-row">
                      <strong data-testid="sv2-address-card-title">
                        {deliveryMethodTitle(lang, addr.delivery_type ?? "NOVA_POSHTA_BRANCH")}
                        {addr.is_default ? (lang === "en" ? " · primary" : " · основна") : ""}
                      </strong>
                    </div>
                    <div className="sv2-muted" style={{ fontSize: 13 }}>
                      {[addr.name, addr.surname].filter(Boolean).join(" ")}
                    </div>
                    {addr.phone ? <div style={{ fontSize: 13 }}>{addr.phone}</div> : null}
                    <div style={{ marginTop: 4, fontSize: 13 }}>
                      {addr.city
                        ? addr.city.startsWith("м.") || addr.city.startsWith("м ")
                          ? addr.city
                          : `м. ${addr.city}`
                        : ""}
                    </div>
                    <div style={{ marginTop: 2, fontSize: 13 }} data-testid="sv2-address-card-destination">
                      {formatDeliveryDestinationLine(addr)}
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
                      <button
                        type="button"
                        className="sv2-cta sv2-secondary sv2-cta-compact"
                        data-testid="sv2-address-use"
                        onClick={() => setAddressBook(setDefaultSavedAddress(addr.id))}
                      >
                        {lang === "en" ? "Use" : "Використати"}
                      </button>
                      {addr.is_default ? (
                        <span className="sv2-pill" data-testid="sv2-address-primary-badge">
                          {lang === "en" ? "Primary" : "Основна"}
                        </span>
                      ) : null}
                      <button
                        type="button"
                        className="sv2-cta sv2-secondary sv2-cta-compact"
                        data-testid="sv2-address-edit"
                        onClick={() => {
                          const draft: AddressBookEditorDraft = {
                            id: addr.id,
                            label: addr.label,
                            name: addr.name,
                            surname: addr.surname,
                            phone: addr.phone,
                            city: addr.city,
                            warehouse: addr.warehouse,
                            is_default: addr.is_default,
                            delivery_type: addr.delivery_type ?? "NOVA_POSHTA_BRANCH",
                          };
                          if (addr.street) draft.street = addr.street;
                          if (addr.building) draft.building = addr.building;
                          if (addr.apartment) draft.apartment = addr.apartment;
                          if (addr.entrance) draft.entrance = addr.entrance;
                          if (addr.floor) draft.floor = addr.floor;
                          if (addr.intercom) draft.intercom = addr.intercom;
                          if (addr.comment) draft.comment = addr.comment;
                          if (addr.display_address) draft.display_address = addr.display_address;
                          if (addr.address_source) draft.address_source = addr.address_source;
                          if (addr.np_selection) draft.np_selection = addr.np_selection;
                          setAddressEditor(draft);
                        }}
                      >
                        {lang === "en" ? "Edit" : "Редагувати"}
                      </button>
                      <button
                        type="button"
                        className="sv2-cta sv2-destructive sv2-cta-compact"
                        data-testid="sv2-address-delete"
                        onClick={() => setArchiveConfirmId(addr.id)}
                      >
                        {lang === "en" ? "Delete" : "Видалити"}
                      </button>
                    </div>
                  </div>
                ))
              )}
              {addressBook.length > 0 ? (
              <button
                type="button"
                className="sv2-cta"
                style={{ width: "100%", marginTop: 12 }}
                data-testid="sv2-address-add"
                onClick={() => {
                  setAddressBook(readAddressBook());
                  setAddressEditor({
                    id: `addr_${Date.now().toString(36)}`,
                    label: lang === "en" ? "Branch" : "Відділення",
                    name: profileName || "",
                    surname: profileSurname || "",
                    phone: profilePhone || "",
                    city: "",
                    warehouse: "",
                    is_default: addressBook.length === 0,
                    delivery_type: "NOVA_POSHTA_BRANCH",
                  });
                }}
              >
                {lang === "en" ? "+ Add address" : "+ Додати адресу"}
              </button>
              ) : null}
              </div>
            </div>
          ) : (
          <div className="sv2-cabinet-grid" data-testid="sv2-cabinet-grid">
            {onOpenProfileForm ? (
              <button
                type="button"
                className="sv2-cabinet-tile"
                data-testid="sv2-cabinet-tile-profile"
                onClick={onOpenProfileForm}
              >
                <span className="sv2-cabinet-tile-emoji" aria-hidden="true">👤</span>
                <span className="sv2-cabinet-tile-label">
                  {lang === "en" ? "Profile" : "Профіль"}
                </span>
              </button>
            ) : null}
            {onOpenDeliveryAddresses ? (
              <button
                type="button"
                className="sv2-cabinet-tile"
                data-testid="sv2-cabinet-tile-delivery"
                onClick={onOpenDeliveryAddresses}
              >
                <span className="sv2-cabinet-tile-emoji" aria-hidden="true">📍</span>
                <span className="sv2-cabinet-tile-label">
                  {lang === "en" ? "My delivery addresses" : "Мої адреси доставки"}
                </span>
              </button>
            ) : null}
            {onOpenOrders ? (
              <button
                type="button"
                className="sv2-cabinet-tile"
                data-testid="sv2-cabinet-tile-orders"
                onClick={() => {
                  emitEvent("cabinet_opened", {});
                  onOpenOrders();
                }}
              >
                <span className="sv2-cabinet-tile-emoji" aria-hidden="true">📦</span>
                <span className="sv2-cabinet-tile-label">
                  {lang === "en" ? "My orders" : "Мої замовлення"}
                </span>
              </button>
            ) : null}
            {onOpenMessages ? (
              <button
                type="button"
                className="sv2-cabinet-tile"
                data-testid="sv2-cabinet-tile-messages"
                onClick={() => {
                  emitEvent("cabinet_opened", {});
                  onOpenMessages();
                }}
              >
                <span className="sv2-cabinet-tile-emoji" aria-hidden="true">🔔</span>
                <span className="sv2-cabinet-tile-label">
                  {lang === "en" ? "My messages" : "Мої повідомлення"}
                </span>
              </button>
            ) : null}
            {onOpenInvite ? (
              <button
                type="button"
                className="sv2-cabinet-tile"
                data-testid="sv2-cabinet-tile-invite"
                onClick={onOpenInvite}
              >
                <span className="sv2-cabinet-tile-emoji" aria-hidden="true">🎁</span>
                <span className="sv2-cabinet-tile-label">
                  {lang === "en" ? "Invite friends" : "Запросити друзів"}
                </span>
              </button>
            ) : null}
            {onOpenEngagementHub ? (
              <button
                type="button"
                className="sv2-cabinet-tile"
                data-testid="sv2-cabinet-tile-engagement"
                onClick={onOpenEngagementHub}
              >
                <span className="sv2-cabinet-tile-emoji" aria-hidden="true">🧭</span>
                <span className="sv2-cabinet-tile-label">
                  {lang === "en" ? "My adventures" : "Мої пригоди"}
                </span>
              </button>
            ) : null}
            {onOpenCart ? (
              <button
                type="button"
                className="sv2-cabinet-tile"
                data-testid="sv2-cabinet-tile-cart"
                onClick={onOpenCart}
              >
                <span className="sv2-cabinet-tile-emoji" aria-hidden="true">🛒</span>
                <span className="sv2-cabinet-tile-label">
                  {lang === "en" ? "My cart" : "Мій кошик"}
                </span>
              </button>
            ) : null}
            <button
              type="button"
              className="sv2-cabinet-tile"
              data-testid="sv2-cabinet-tile-docs"
              onClick={() => {
                if (cabinetSessionKey && onOpenOrderDocuments) {
                  openOrderDocsHome();
                  return;
                }
                openDocsHub();
              }}
            >
              <span className="sv2-cabinet-tile-emoji" aria-hidden="true">📄</span>
              <span className="sv2-cabinet-tile-label">
                {lang === "en" ? "Documents" : "Документи"}
              </span>
            </button>
          </div>
          )}
        </>

      {marketingSuppressed || deleteRequested || privacyMsg ? (
        <div className="sv2-alert sv2-alert-ok" style={{ marginTop: 14 }}>
          {privacyMsg ||
            (lang === "en"
              ? "Marketing disabled / data processing request"
              : "Маркетинг вимкнено / запит на обробку даних")}
        </div>
      ) : null}

      {cabinetSurface === "root" ? (
      <div className="sv2-social">
        <button type="button" className="sv2-social-btn" disabled={!social.instagram}>Instagram</button>
        <button type="button" className="sv2-social-btn" disabled={!social.youtube}>YouTube</button>
        <button type="button" className="sv2-social-btn" disabled={!social.tiktok}>TikTok</button>
      </div>
      ) : null}

      {buildId && !hideTechnicalDiagnostics ? (
        <div className="sv2-muted" style={{ textAlign: "center", marginTop: 10, fontSize: 11 }}>
          {lang === "en" ? "Build" : "Збірка"}: {buildId}
          {snapshotId ? (
            <>
              <br />
              {lang === "en" ? "Catalog" : "Каталог"}: {snapshotId}
              {catalogDate ? ` · ${catalogDate}` : ""}
            </>
          ) : null}
        </div>
      ) : snapshotId && !hideTechnicalDiagnostics ? (
        <div className="sv2-muted" style={{ textAlign: "center", marginTop: 10, fontSize: 11 }}>
          {lang === "en" ? "Catalog" : "Каталог"}: {snapshotId}
          {catalogDate ? ` · ${catalogDate}` : ""}
        </div>
      ) : null}

      {onCloseStore && !panel && cabinetSurface === "root" ? (
        <div className="sv2-post-order-nav" style={{ marginTop: 16 }}>
          <TertiaryTextAction testId="sv2-close-store-profile" onClick={onCloseStore}>
            {lang === "en" ? "Close store" : "Закрити магазин"}
          </TertiaryTextAction>
        </div>
      ) : null}

      {addressEditor ? (
        <div data-testid="sv2-address-editor-host">
          <AddressBookEditorSheetV2
            lang={lang}
            initial={addressEditor}
            onCancel={() => setAddressEditor(null)}
            onSave={(draft) => {
              setAddressBook(upsertSavedAddress(draft));
              setAddressEditor(null);
            }}
          />
        </div>
      ) : null}
      {archiveConfirmId ? (
        <div
          className="sv2-modal-overlay"
          role="dialog"
          aria-modal="true"
          data-testid="sv2-address-archive-confirm"
        >
          <div className="sv2-modal-card">
            <p style={{ marginTop: 0 }}>
              {lang === "en" ? "Archive this address?" : "Архівувати цю адресу?"}
            </p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                type="button"
                className="sv2-cta sv2-destructive"
                data-testid="sv2-address-archive-yes"
                onClick={() => {
                  setAddressBook(archiveSavedAddress(archiveConfirmId));
                  setArchiveConfirmId(null);
                }}
              >
                {lang === "en" ? "Archive" : "Архівувати"}
              </button>
              <button
                type="button"
                className="sv2-cta sv2-secondary"
                data-testid="sv2-address-archive-no"
                onClick={() => setArchiveConfirmId(null)}
              >
                {lang === "en" ? "Cancel" : "Скасувати"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <LegalDetailFooterNav
        lang={lang}
        cartCount={cartCount}
        onBack={legalBack}
        {...(onOpenCart ? { onCart: onOpenCart } : {})}
        {...(onOpenCatalog ? { onCatalog: onOpenCatalog } : {})}
        testId="sv2-legal-hub-footer-nav"
      />
    </div>
  );
}
